/**
 * Online MethodSCRIPT Editor - Main Entry Point
 *
 * This file initializes the Monaco editor with MethodScript language support.
 * It uses AMD/RequireJS for dynamic Monaco loading which TypeScript doesn't understand,
 * so type checking is disabled for the AMD-specific sections.
 */

import { rawData } from './completion-data.js';
import { languageConfig, monarchTokensProvider, methodscriptTheme } from '../public/shared-monaco/monaco-language-config.js';
import { MethodScriptCompletionProvider, createCodeActionProvider, VARTYPES } from '../public/shared-monaco/index.js';
import { MethodScriptFormatterService } from './formatter.js';

// URL utilities
import { loadScriptFromQuerystring, loadScriptFromHash, validateMethodScriptFormat, createShareableUrl } from './script-url-utils.js';
import { logger } from 'shared-monaco-methodscript';

// Monaco providers
import { createMethodScriptHoverProvider, createMethodScriptSignatureHelpProvider, createMethodScriptFoldingRangeProvider } from './monaco-providers.js';

// Validation
import { validateCode, getCurrentDiagnostics } from './validation.js';

// Device communication
import { initializeDeviceCommunication, parseAndDisplayDataPackages, type DeviceManagers } from './device-communication-setup.js';

// Re-export MessageType for testing
import { MessageType } from './device-communication.js';

/** Debounce delay (ms) for auto-validation after editor content changes */
const VALIDATION_DEBOUNCE_MS = 500;

/** Duration (ms) to show the "Copied!" feedback on the share button */
const SHARE_BUTTON_FEEDBACK_MS = 2000;

// LZString for window exposure (debugging/testing)
import type { LZStringStatic } from './lz-string.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error - LZ-string wrapper is a vendor file without types
import LZStringImport from '../public/vendor/lz-string-wrapper.js';
const LZString = LZStringImport as LZStringStatic;

// Monaco types (will be available after AMD load)
import type * as monacoTypes from 'monaco-editor';

// Global declarations for AMD loader and Monaco
/* eslint-disable @typescript-eslint/no-explicit-any */
declare const require: any;
declare const monaco: typeof monacoTypes;
declare global {
    interface Window {
        LZString: LZStringStatic;
        editor: monacoTypes.editor.IStandaloneCodeEditor;
        validateCode: () => Promise<void>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        completionData: any[];
        rawData: typeof rawData;
        webserialManager: unknown;
        webbleManager: unknown;
        communicationPanel: unknown;
        testCodeActionProvider: unknown;
        parseAndDisplayDataPackages: unknown;
        MessageType: unknown;
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Completion item structure
 */
interface CompletionItem {
    label: string;
    kind: number;
    detail: string;
    documentation: string;
    insertText: string;
    insertTextRules?: number;
}

// Module state
let editor: monacoTypes.editor.IStandaloneCodeEditor;
let completionData: CompletionItem[] = [];
let completionProvider: MethodScriptCompletionProvider;
let formatterService: MethodScriptFormatterService;
let deviceManagers: DeviceManagers | null = null;

// Expose LZString to window for debugging/testing
if (typeof window !== 'undefined') {
    window.LZString = LZString;
}

/**
 * Update status display
 */
function updateStatus(message: string, isError: boolean): void {
    const statusEl = document.getElementById('status');
    if (statusEl) {
        statusEl.textContent = message;
        statusEl.className = isError ? 'error' : 'success';
    }
}

/**
 * Update editor content with new script
 */
function updateEditorContent(script: string, validate = true): boolean {
    if (!editor || !script) {
        return false;
    }

    // Validate script format if requested
    if (validate) {
        const validation = validateMethodScriptFormat(script);
        if (!validation.valid) {
            updateStatus(`Invalid script format: ${validation.error}`, true);
            logger.warn('Script validation failed:', validation.error);
            return false; // Don't load invalid script
        }
    }

    editor.setValue(script);
    updateStatus('Script loaded from URL', false);
    return true;
}

/**
 * Share the current script by creating a shareable URL
 */
function shareScript(): void {
    const code = editor.getValue();

    if (!code || code.trim() === '') {
        updateStatus('Nothing to share - editor is empty', true);
        return;
    }

    try {
        const shareUrl = createShareableUrl(code);

        // Copy to clipboard
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
                // Visual feedback: change button appearance
                const shareBtn = document.getElementById('shareBtn');
                if (shareBtn) {
                    const originalText = shareBtn.textContent;
                    shareBtn.textContent = 'Copied!';
                    shareBtn.classList.add('copied');

                    updateStatus('Link copied to clipboard!', false);

                    // Reset button after feedback duration
                    setTimeout(() => {
                        shareBtn.textContent = originalText;
                        shareBtn.classList.remove('copied');
                    }, SHARE_BUTTON_FEEDBACK_MS);
                }
            })
            .catch((err) => {
                logger.error('Failed to copy to clipboard:', err);
                updateStatus('Failed to copy link - please copy manually', true);

                // Fallback: show the URL in a prompt
                prompt('Copy this shareable link:', shareUrl);
            });
    } catch (error) {
        logger.error('Failed to create share link:', error);
        updateStatus('Failed to create share link', true);
    }
}

/**
 * Set up button event handlers
 */
function setupButtonHandlers(): void {
    document.getElementById('formatBtn')?.addEventListener('click', () => {
        editor.getAction('editor.action.formatDocument')?.run();
    });

    document.getElementById('clearBtn')?.addEventListener('click', () => {
        editor.setValue('');
    });

    document.getElementById('shareBtn')?.addEventListener('click', () => {
        shareScript();
    });
}

/**
 * Build completion data from raw data
 */
function buildCompletionData(monacoInstance: typeof monacoTypes): CompletionItem[] {
    const data: CompletionItem[] = [];

    // Add keywords
    for (const item of rawData.keywords) {
        data.push({
            label: item.label,
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            detail: 'Keyword',
            documentation: item.doc,
            insertText: item.snippet || item.label,
            insertTextRules: item.snippet ? monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
        });
    }

    // Add measurement commands
    for (const item of rawData.measurementCommands) {
        data.push({
            label: item.label,
            kind: monacoInstance.languages.CompletionItemKind.Function,
            detail: 'Measurement loop command',
            documentation: item.doc,
            insertText: item.snippet || item.label,
            insertTextRules: item.snippet ? monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
        });
    }

    // Add standard commands
    for (const item of rawData.standardCommands) {
        data.push({
            label: item.label,
            kind: monacoInstance.languages.CompletionItemKind.Function,
            detail: 'MethodScript command',
            documentation: item.doc,
            insertText: item.snippet || item.label,
            insertTextRules: item.snippet ? monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
        });
    }

    // Add VarTypes (from shared package - single source of truth)
    for (const item of VARTYPES) {
        data.push({
            label: item.label,
            kind: monacoInstance.languages.CompletionItemKind.EnumMember,
            detail: item.doc,
            documentation: item.doc,
            insertText: item.label,
        });
    }

    return data;
}

/**
 * Initialize the Monaco editor and all language features
 */
function initializeEditor(monacoInstance: typeof monacoTypes): void {
    // Build completion data
    completionData = buildCompletionData(monacoInstance);

    // Register MethodScript language
    monacoInstance.languages.register({ id: 'methodscript' });

    // Set language configuration
    monacoInstance.languages.setLanguageConfiguration('methodscript', languageConfig);

    // Set Monarch tokenizer
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    monacoInstance.languages.setMonarchTokensProvider('methodscript', monarchTokensProvider as any);

    // Define theme
    monacoInstance.editor.defineTheme('methodscript-dark', methodscriptTheme);

    // Create completion provider instance
    completionProvider = new MethodScriptCompletionProvider({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        completionData: completionData as any,
        monacoCompletionItemKind: monacoInstance.languages.CompletionItemKind,
    });

    // Create editor
    const editorContainer = document.getElementById('editor');
    if (!editorContainer) {
        throw new Error('Editor container not found');
    }

    editor = monacoInstance.editor.create(editorContainer, {
        value: 'e\n\n',
        language: 'methodscript',
        theme: 'methodscript-dark',
        minimap: { enabled: true },
        fontSize: 14,
        lineNumbers: (lineNumber: number) => String(lineNumber - 1), // Start from 0 (e line is not counted in errors)
        roundedSelection: false,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        suggest: {
            showWords: false, // Disable word-based completions from open editors
            showInlineDetails: true, // Show details inline for all items
        },
    });

    // Register completion provider using the shared provider
    monacoInstance.languages.registerCompletionItemProvider('methodscript', {
        triggerCharacters: ['.', '_', 'm', ' '],
        provideCompletionItems: (model, position) => {
            return completionProvider.provideCompletionItems(model, position);
        },
    });

    // Register code action provider for quick fixes using shared provider
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const codeActionProvider = createCodeActionProvider(() => getCurrentDiagnostics() as any, monacoInstance);
    monacoInstance.languages.registerCodeActionProvider('methodscript', codeActionProvider);

    // Expose for testing
    window.testCodeActionProvider = codeActionProvider;

    // Register hover provider
    monacoInstance.languages.registerHoverProvider('methodscript', createMethodScriptHoverProvider(monacoInstance));

    // Register signature help provider
    monacoInstance.languages.registerSignatureHelpProvider('methodscript', createMethodScriptSignatureHelpProvider());

    // Register folding range provider
    monacoInstance.languages.registerFoldingRangeProvider('methodscript', createMethodScriptFoldingRangeProvider(monacoInstance));

    // Register formatting provider
    monacoInstance.languages.registerDocumentFormattingEditProvider('methodscript', {
        provideDocumentFormattingEdits: async (model) => {
            const text = model.getValue();

            // Use the formatter service (calls C# via LSP or WASM)
            if (!formatterService) {
                formatterService = new MethodScriptFormatterService();
            }

            const formattedText = await formatterService.formatDocument(text);

            // Return a single edit that replaces the entire document
            if (formattedText !== text) {
                const lastLine = model.getLineCount();
                const lastColumn = model.getLineMaxColumn(lastLine);

                return [
                    {
                        range: new monacoInstance.Range(1, 1, lastLine, lastColumn),
                        text: formattedText,
                    },
                ];
            }

            return [];
        },
    });

    // Add keyboard shortcut for formatting (Ctrl+Shift+F)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyF, () => {
        editor.getAction('editor.action.formatDocument')?.run();
    });

    // Add keyboard shortcut for formatting (Ctrl+S)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS, () => {
        editor.getAction('editor.action.formatDocument')?.run();
    });

    // Create validation callback
    const validateCallback = async () => {
        await validateCode(editor, monacoInstance, updateStatus);
    };

    // Debounced auto-validation
    let validationTimeout: ReturnType<typeof setTimeout>;
    editor.onDidChangeModelContent(() => {
        clearTimeout(validationTimeout);
        validationTimeout = setTimeout(() => {
            validateCallback();
        }, VALIDATION_DEBOUNCE_MS);
    });

    // Set up button handlers
    setupButtonHandlers();

    // Load script from querystring or hash if present
    const scriptFromUrl = loadScriptFromQuerystring();
    const scriptFromHash = loadScriptFromHash();

    if (scriptFromUrl) {
        updateEditorContent(scriptFromUrl);
        // Don't call validateCode() here - if loading succeeded, the loaded script
        // will be validated automatically via onDidChangeModelContent.
        // If validation failed, we want to keep the validation error message visible.
    } else if (scriptFromHash) {
        // Load from hash on initial load (for bookmark/direct navigation)
        updateEditorContent(scriptFromHash);
    } else {
        // Initial validation only if no script from URL or hash
        validateCallback();
    }

    // Initialize device communication
    deviceManagers = initializeDeviceCommunication(editor, monacoInstance, updateStatus);

    // Listen for hashchange events to support loading scripts without page reload
    window.addEventListener('hashchange', () => {
        const newScriptFromHash = loadScriptFromHash();
        if (newScriptFromHash) {
            updateEditorContent(newScriptFromHash);
        }
    });

    // Expose globals for testing
    window.editor = editor;
    window.validateCode = validateCallback;
    window.completionData = completionData;
    window.rawData = rawData;
    window.currentDiagnostics = getCurrentDiagnostics();

    // Expose device communication for tests
    if (deviceManagers) {
        window.webserialManager = deviceManagers.webserialManager;
        window.webbleManager = deviceManagers.webbleManager;
        window.communicationPanel = deviceManagers.communicationPanel;

        // Expose parsing functions and MessageType for testing
        window.parseAndDisplayDataPackages = (data: string) => {
            parseAndDisplayDataPackages(data, deviceManagers!.packageParser, deviceManagers!.communicationPanel);
        };
        window.MessageType = MessageType;
    }
}

// Load Monaco Editor using AMD/RequireJS
require.config({ paths: { vs: '/monaco-editor/min/vs' } });

require(['vs/editor/editor.main'], function () {
    // Monaco is now available as global 'monaco'
    initializeEditor(monaco);
});
